import defaultTablesCsvRaw from "../data/tablesCsv";
import defaultTableMappingsCsvRaw from "../data/tableMappingsCsv";
import defaultColumnMappingsCsvRaw from "../data/columnMappingsCsv";

import { parseCsv } from "./csv";
import type {
  ColumnMappingRecord,
  DataVaultModel,
  HubEntity,
  LayoutMap,
  LinkEntity,
  ManualEntity,
  SatelliteEntity,
  TableMappingRecord,
  TableRecord
} from "./types";
import type { IDataVaultRelatedEntity } from "../../../interfaces/dataVaultModelling.interfaces";
import {
  getConstructTypeForState,
  isHubConstruct,
  isLinkConstruct,
  isManualConstruct,
  isSatelliteConstruct
} from "./constructs";

function buildLinks(
  tables: TableRecord[],
  tableMappings: TableMappingRecord[],
  columnMappings: ColumnMappingRecord[],
  hubById: Map<string, HubEntity>,
  linkConnections: Map<string, string[]>,
  linkHubMetadata: Map<string, LinkEntity["linkedHubs"]>
): LinkEntity[] {
  const links: LinkEntity[] = [];

  tables
    .filter((table) => isLinkConstruct(table.datavault_construct_type))
    .forEach((table) => {
      const baseName = table.target_table.replace(/_l$/i, "");
      const inferredConnected = baseName
        .split("__")
        .map((segment) => `${segment}_h`)
        .filter((candidate) => hubById.has(candidate));
      const apiLinked = linkConnections.get(table.target_table) ?? [];
      const linkedFromApi = apiLinked
        .map((targetName) => targetName && hubById.has(targetName) ? targetName : null)
        .filter((value): value is string => Boolean(value));
      const connectedHubIds = linkedFromApi.length
        ? Array.from(new Set(linkedFromApi))
        : Array.from(new Set(inferredConnected));
      const linkedHubs = linkHubMetadata.get(table.target_table);
      const linkMemberIdsByHubId =
        linkedHubs?.reduce<Record<string, number>>((acc, linked) => {
          const hubId = linked.hub_target_entity_rdv_name ?? "";
          if (hubId && linked.link_member_id != null) {
            acc[hubId] = linked.link_member_id;
          }
          return acc;
        }, {}) ?? {};

      const linkEntity: LinkEntity = {
        id: table.target_table,
        record: table,
        connectedHubIds,
        mappings: tableMappings.filter(
          (mapping) => mapping.target_table === table.target_table
        ),
        columns: columnMappings.filter(
          (column) => column.target_table === table.target_table
        ),
        satelliteIds: [],
        linkedHubs,
        linkMemberIdsByHubId
      };

      links.push(linkEntity);
    });

  return links;
}

function buildModelFromRecords(
  tables: TableRecord[],
  tableMappings: TableMappingRecord[],
  columnMappings: ColumnMappingRecord[],
  linkConnections: Map<string, string[]>,
  linkHubMetadata: Map<string, LinkEntity["linkedHubs"]>,
  satelliteParentsByTargetTable: Map<string, string>,
  satelliteParentsByTargetId: Map<string, string>,
  satelliteMemberIdsByTargetTable: Map<string, number>,
  satelliteMemberIdsByTargetId: Map<string, number>
): DataVaultModel {
  const hubs: HubEntity[] = tables
    .filter((table) => isHubConstruct(table.datavault_construct_type))
    .map((table) => ({
      id: table.target_table,
      record: table,
      mappings: tableMappings.filter(
        (mapping) => mapping.target_table === table.target_table
      ),
      columns: columnMappings.filter(
        (column) => column.target_table === table.target_table
      )
    }));

  const hubByEnsemble = new Map<string, HubEntity[]>();
  hubs.forEach((hub) => {
    hubByEnsemble.set(hub.record.ensemble, [
      ...(hubByEnsemble.get(hub.record.ensemble) ?? []),
      hub
    ]);
  });

  const hubById = new Map(hubs.map((hub) => [hub.id, hub]));

  const satellites: SatelliteEntity[] = tables
    .filter((table) => isSatelliteConstruct(table.datavault_construct_type))
    .map((table) => {
      let parentHubId =
        satelliteParentsByTargetTable.get(table.target_table) ??
        (table.target_entity_rdv_id
          ? satelliteParentsByTargetId.get(table.target_entity_rdv_id)
          : undefined) ??
        null;

      const ensembleHubs = hubByEnsemble.get(table.ensemble) ?? [];
      if (!parentHubId) {
        parentHubId = ensembleHubs[0]?.id ?? null;
      }
      if (!parentHubId) {
        const derivedHubId = table.target_table.replace(/_s$/i, "_h");
        if (hubById.has(derivedHubId)) {
          parentHubId = derivedHubId;
        }
      }
      return {
        id: table.target_table,
        record: table,
        parentHubId,
        satMemberId:
          satelliteMemberIdsByTargetTable.get(table.target_table) ??
          (table.target_entity_rdv_id
            ? satelliteMemberIdsByTargetId.get(table.target_entity_rdv_id)
            : undefined) ??
          null,
        mappings: tableMappings.filter(
          (mapping) => mapping.target_table === table.target_table
        ),
        columns: columnMappings.filter(
          (column) => column.target_table === table.target_table
        )
      };
    });

  const links = buildLinks(
    tables,
    tableMappings,
    columnMappings,
    hubById,
    linkConnections,
    linkHubMetadata
  );

  const manual: ManualEntity[] = tables
    .filter((table) => isManualConstruct(table.datavault_construct_type))
    .map((table) => ({
      id: table.target_table,
      record: table,
      mappings: tableMappings.filter(
        (mapping) => mapping.target_table === table.target_table
      ),
      columns: columnMappings.filter(
        (column) => column.target_table === table.target_table
      )
    }));

  return {
    hubs,
    satellites,
    links,
    manual
  };
}

export function buildModelFromCsv(
  tablesCsvRaw: string,
  tableMappingsCsvRaw: string,
  columnMappingsCsvRaw: string
): DataVaultModel {
  const tables = parseCsv<TableRecord>(tablesCsvRaw);
  const tableMappings = parseCsv<TableMappingRecord>(tableMappingsCsvRaw);
  const columnMappings = parseCsv<ColumnMappingRecord>(columnMappingsCsvRaw);

  return buildModelFromRecords(
    tables,
    tableMappings,
    columnMappings,
    new Map(),
    new Map(),
    new Map(),
    new Map(),
    new Map(),
    new Map()
  );
}

const normalizeConstructType = (value: string | null): TableRecord["datavault_construct_type"] => {
  const normalized = value?.toLowerCase() ?? "";
  const isView = normalized.includes("view");
  if (normalized.includes("sat")) {
    return getConstructTypeForState("satellite", isView);
  }
  if (normalized.includes("link")) {
    return getConstructTypeForState("link", isView);
  }
  if (normalized.includes("hub")) {
    return getConstructTypeForState("hub", isView);
  }
  // Anything else — explicit "Manual"/BDV or an unrecognized construct — must NOT
  // silently become a hub. A hub with no business keys breaks hub/link rendering.
  // Render it as a distinct, non-generated manual node instead.
  if (!normalized) {
    return getConstructTypeForState("hub", isView);
  }
  return "Manual";
};

const sanitizeString = (value: string | null | undefined) => (value ?? "").trim();

const resolveTargetTableName = (record: IDataVaultRelatedEntity): string | null => {
  const targetName = sanitizeString(record.target_entity_rdv_name);
  if (targetName) {
    return targetName;
  }
  if (record.target_entity_rdv_id != null) {
    return `target_${record.target_entity_rdv_id}`;
  }
  return null;
};

const parseCanvasCoordinate = (value: unknown): number | null => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === "string") {
    const parsed = Number(value.trim());
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

const buildDescription = (record: IDataVaultRelatedEntity) => {
  const sourceName = sanitizeString(record.source_entity_name);
  const dataSource = sanitizeString(record.data_source_name);
  if (!sourceName && !dataSource) {
    return "";
  }
  if (sourceName && dataSource) {
    return `${sourceName} from ${dataSource}`;
  }
  return sourceName || dataSource;
};

function buildTableRecordFromEntity(
  record: IDataVaultRelatedEntity,
  defaultDwTargetType?: string | null
): TableRecord | null {
  const targetName = sanitizeString(record.target_entity_rdv_name);
  const targetTable = targetName || resolveTargetTableName(record);

  if (!targetTable) {
    return null;
  }

  const includeDqTest = record.include_dq_test ?? true;
  const resolvedDwTargetType =
    sanitizeString(record.dw_target_types) || (defaultDwTargetType ?? "");
  return {
    subject_area: sanitizeString(record.subject_area),
    ensemble: sanitizeString(record.ensemble),
    target_schema: sanitizeString(record.target_schema) || "rdv",
    target_table: targetTable,
    target_entity_rdv_name: targetName || targetTable,
    target_entity_rdv_id: record.target_entity_rdv_id
      ? String(record.target_entity_rdv_id)
      : undefined,
    datavault_construct_type: normalizeConstructType(record.table_construct),
    // Read the stored distribution — hardcoding "" made the blade render the
    // ROUND_ROBIN fallback for every entity regardless of what the database held.
    distribution_type: sanitizeString(record.distribution_type),
    distribution_key: sanitizeString(record.distribution_key),
    dw_target_types: resolvedDwTargetType || undefined,
    dw_target_types_onprem: sanitizeString(record.dw_target_types_onprem) || undefined,
    exclude_metadata_tests: record.skip_metadata_tests ? "Y" : "",
    include_dq_test: includeDqTest ? "Y" : "",
    generate_model_entity: record.generate_model_entity ? "Y" : "",
    is_custom_view_or_table: record.is_custom_view_or_table ? "Y" : "",
    dq_source_filter: sanitizeString(record.dq_source_filter),
    Description: buildDescription(record),
    data_source_name: sanitizeString(record.data_source_name),
    data_source_type: sanitizeString(record.data_source_type),
    source_entity_name: sanitizeString(record.source_entity_name),
    source_entity_id: record.source_entity_id ? String(record.source_entity_id) : "",
    source_filter: sanitizeString(record.source_filter)
  };
}

function buildTableMappingFromEntity(
  record: IDataVaultRelatedEntity,
  targetTable: string
): TableMappingRecord {
  return {
    ensemble: sanitizeString(record.ensemble),
    data_source_name: sanitizeString(record.data_source_name),
    source_schema: sanitizeString(record.data_source_type),
    source_table: sanitizeString(record.source_entity_name),
    source_filter: sanitizeString(record.source_filter),
    target_schema: sanitizeString(record.target_schema) || "rdv",
    target_table: targetTable,
    exclude_automation: record.skip_metadata_tests ? "Y" : "",
    Description: buildDescription(record)
  };
}

export function buildModelFromRelatedEntities(
  relatedEntities: IDataVaultRelatedEntity[],
  options?: { defaultDwTargetType?: string | null }
): DataVaultModel {
  if (!relatedEntities?.length) {
    return {
      hubs: [],
      satellites: [],
      links: [],
      manual: []
    };
  }

  const defaultDwTargetType = options?.defaultDwTargetType ?? null;
  const tableRecords = new Map<string, TableRecord>();
  const tableMappings: TableMappingRecord[] = [];
  const columnMappings: ColumnMappingRecord[] = [];
  const mappingKeys = new Set<string>();
  const linkConnections = new Map<string, string[]>();
  const linkHubMetadata = new Map<string, LinkEntity["linkedHubs"]>();
  const satelliteParentsByTargetTable = new Map<string, string>();
  const satelliteParentsByTargetId = new Map<string, string>();
  const satelliteMemberIdsByTargetTable = new Map<string, number>();
  const satelliteMemberIdsByTargetId = new Map<string, number>();

  relatedEntities.forEach((entity) => {
    const tableRecord = buildTableRecordFromEntity(entity, defaultDwTargetType);
    if (!tableRecord) {
      return;
    }

    const existingRecord = tableRecords.get(tableRecord.target_table);
    if (!existingRecord) {
      tableRecords.set(tableRecord.target_table, tableRecord);
    } else {
      (Object.keys(tableRecord) as (keyof TableRecord)[]).forEach((key) => {
        if (!existingRecord[key] && tableRecord[key]) {
          // @ts-expect-error - TS cannot narrow the assignment for readonly union keys
          existingRecord[key] = tableRecord[key];
        }
      });
    }

    if (entity.linked_hubs?.length && isLinkConstruct(tableRecord.datavault_construct_type)) {
      const hubNames = entity.linked_hubs
        .map((linked) => sanitizeString(linked.hub_target_entity_rdv_name))
        .filter((name) => Boolean(name));
      if (hubNames.length) {
        linkConnections.set(tableRecord.target_table, hubNames);
      }
      linkHubMetadata.set(
        tableRecord.target_table,
        entity.linked_hubs.map((linked) => ({
          hub_target_entity_rdv_id: linked.hub_target_entity_rdv_id
            ? String(linked.hub_target_entity_rdv_id)
            : null,
          hub_target_entity_rdv_name: sanitizeString(linked.hub_target_entity_rdv_name) || null,
          hub_role: linked.hub_role ?? null,
          link_member_id: linked.link_member_id ?? null,
          is_active: linked.is_active ?? null
        }))
      );
    }

    if (entity.sat_hubs?.length && isHubConstruct(tableRecord.datavault_construct_type)) {
      entity.sat_hubs.forEach((satellite) => {
        const satName = sanitizeString(satellite.sat_target_entity_rdv_name);
        const satId =
          satellite.sat_target_entity_rdv_id != null
            ? String(satellite.sat_target_entity_rdv_id)
            : "";
        if (satName) {
          satelliteParentsByTargetTable.set(satName, tableRecord.target_table);
          if (satellite.sat_member_id != null) {
            satelliteMemberIdsByTargetTable.set(satName, satellite.sat_member_id);
          }
        }
        if (satId) {
          satelliteParentsByTargetId.set(satId, tableRecord.target_table);
          if (satellite.sat_member_id != null) {
            satelliteMemberIdsByTargetId.set(satId, satellite.sat_member_id);
          }
        }
      });
    }

    const mapping = buildTableMappingFromEntity(entity, tableRecord.target_table);
    const key = `${mapping.data_source_name}|${mapping.source_schema}|${mapping.source_table}|${mapping.target_table}`;
    if (!mappingKeys.has(key)) {
      mappingKeys.add(key);
      tableMappings.push(mapping);
    }
  });

  return buildModelFromRecords(
    Array.from(tableRecords.values()),
    tableMappings,
    columnMappings,
    linkConnections,
    linkHubMetadata,
    satelliteParentsByTargetTable,
    satelliteParentsByTargetId,
    satelliteMemberIdsByTargetTable,
    satelliteMemberIdsByTargetId
  );
}

export function buildModel(): DataVaultModel {
  return buildModelFromCsv(
    defaultTablesCsvRaw,
    defaultTableMappingsCsvRaw,
    defaultColumnMappingsCsvRaw
  );
}

export function buildLayoutFromRelatedEntities(
  relatedEntities: IDataVaultRelatedEntity[]
): LayoutMap {
  const layout: LayoutMap = {};
  relatedEntities.forEach((entity) => {
    const targetTable = resolveTargetTableName(entity);
    if (!targetTable) {
      return;
    }
    const x = parseCanvasCoordinate(entity.canvas_x);
    const y = parseCanvasCoordinate(entity.canvas_y);
    if (x == null || y == null) {
      return;
    }
    layout[targetTable] = { x, y };
  });
  return layout;
}
